import { TableCard } from "@general-backoffice/core";
import { TableOutput } from "@general-backoffice/core/index";
import type { PaginationMeta, Referral } from "@investhinks/api-client";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { Button, ButtonGroup, Card } from "reactstrap";

import { TypedTable, type TypedTableColumn } from "../../utils/typedLib";
import PaginatedTableFooter from "../shared/PaginatedTableFooter";

interface DefaultReferralFilters extends Record<string, unknown> {
  expired?: boolean | null;
  claimed?: boolean;
}

export interface DefaultReferralsTableCardProps {
  referrals: Referral[];
  totalReferrals: number;
  isLoading: boolean;
  pagination?: PaginationMeta;
  goToPage?: (page: number) => void;
  filters?: DefaultReferralFilters;
  resetFilters?: () => void;
  resetPagination?: () => void;
  setFilters?: (
    filters: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)
  ) => void;
  [key: string]: unknown;
}

const DefaultReferralsTableCard = ({
  referrals,
  totalReferrals,
  isLoading,
  pagination = { page: 1, pageSize: 25, pageCount: 1, total: 0 },
  goToPage = () => {},
  filters = {},
  resetFilters = () => {},
  resetPagination = () => {},
  setFilters = () => {},
}: DefaultReferralsTableCardProps) => {
  const { t } = useTranslation("referral", { keyPrefix: "defaultReferralsTableCard" });

  const columns: TypedTableColumn<Referral>[] = [
    {
      name: t("email"),
      compressed: true,
      formattedContent: ({ referee }) => (
        <>
          <div>{referee?.name}</div>
          <small className="font-weight-bold">{referee?.email}</small>
        </>
      ),
      isTh: true,
    },
    {
      name: "",
      formattedContent: ({ referee }) => {
        const bookmarks = [];
        if (!referee?.isActive)
          bookmarks.push({
            icon: <i className="fas fa-exclamation-triangle" aria-hidden="true" />,
            title: t("refereeInactive"),
          });
        return <TableOutput.Bookmarks bookmarks={bookmarks} />;
      },
    },
    {
      name: t("accessMethod"),
      formattedContent: ({ referral_code }) => referral_code?.name || t("linkAccessMethod"),
    },
    {
      name: t("registerDate"),
      formattedContent: ({ createdAt }) => moment(createdAt).format("LL"),
    },
  ];

  const buttons = [
    {
      children: t("activeFilter"),
      active: !filters?.expired && !filters?.claimed,
      onClick: () => resetFilters(),
    },
    {
      children: t("claimedFilter"),
      active: filters?.claimed,
      onClick: () =>
        setFilters({
          expired: null,
          claimed: true,
        }),
    },
  ];

  return (
    <Card className="overflow-hidden">
      <TableCard.Header
        title={t("title")}
        subtitle={t("subtitle", { numReferrals: totalReferrals })}
        isLoading={isLoading}
      >
        <ButtonGroup>
          {buttons.map((b, index) => (
            <Button
              {...b}
              size={"sm"}
              onClick={() => {
                resetPagination();
                b.onClick();
              }}
              key={index}
              color="default"
              outline
            />
          ))}
        </ButtonGroup>
      </TableCard.Header>
      <TypedTable
        columns={columns}
        rows={referrals}
        isLoading={isLoading}
        noContentMessage={t("emptyReferrals")}
        rowAmount={pagination.pageSize}
        selectable={false}
        sortable={false}
      />
      <PaginatedTableFooter
        {...{
          isLoading,
          pagination,
          goToPage,
        }}
      />
    </Card>
  );
};

export default DefaultReferralsTableCard;
